import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import ChatBubblev1 from "./ChatBubblev1";
import ChatHistory from "./ChatHistory";
import "../../index.css";

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
	return (
		<div role="alert" style={{
			padding: '20px',
			margin: '20px',
			backgroundColor: 'rgba(255, 0, 0, 0.1)',
			borderRadius: '10px',
			color: '#ff4444'
		}}>
			<h3>Something went wrong:</h3>
			<pre>{error.message}</pre>
			<button onClick={resetErrorBoundary} style={{
				padding: '8px 16px',
				backgroundColor: 'rgba(255, 68, 68, 0.1)',
				border: '1px solid #ff4444',
				borderRadius: '4px',
				color: '#ff4444',
				cursor: 'pointer'
			}}>
				Try again
			</button>
		</div>
	);
};

const ChatBubble = (props) => {
	const { chatHistory, client } = props;
	const [isHovered, setIsHovered] = useState(false);
	const [history, setHistory] = useState(1);
	const [session, setSession] = useState("-1");
	const [messages, setMessages] = useState([]);
	const [errorResponse, setErrorResponse] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [language, setLanguage] = useState("en");

	const timer = useRef(null);
	const errorMessage = " Error in retrieving response. Please reset session.";
	const queryClient = useQueryClient();

	// Memoized functions
	const showHistory = useCallback(() => {
		setHistory(prev => !prev);
	}, []);

	const handleSendMessage = useCallback(async (message) => {
		if (!message?.trim() || isProcessing) return;

		try {
			setIsProcessing(true);
			const sanitizedMessage = DOMPurify.sanitize(message);
			const userMessage = { sender: "user", content: sanitizedMessage };

			// Add user message immediately
			setMessages(prevMessages => [...prevMessages, userMessage]);

			// Send message to client
			await client.sendText(sanitizedMessage);

			// Clear any existing audio playback
			if (client.stopRecording) {
				client.stopRecording();
			}
		} catch (error) {
			console.error("Error sending message:", error);
			setErrorResponse(true);
			// Add error message to chat
			setMessages(prevMessages => [...prevMessages, {
				sender: "npc",
				content: "Sorry, there was an error processing your message. Please try again.",
				error: true
			}]);
		} finally {
			setIsProcessing(false);
		}
	}, [client, isProcessing]);

	const ResetHistory = useCallback(() => {
		try {
			const storedData = localStorage.getItem("messages");
			if (storedData) {
				const parsedData = JSON.parse(storedData);
				parsedData[client?.characterId] = {
					sessionID: -1,
					message: [""],
				};
				localStorage.setItem("messages", JSON.stringify(parsedData));
			}

			if (client?.convaiClient?.current) {
				client.convaiClient.current.resetSession();
			}

			setSession("-1");
			setMessages([]);
			client?.setUserText("");
			client?.setNpcText("");
			queryClient.invalidateQueries(['messages']);
		} catch (error) {
			console.error("Error resetting history:", error);
		}
	}, [client, queryClient]);

	// Memoized handlers
	const handleMouseEnter = useCallback(() => setIsHovered(true), []);
	const handleMouseLeave = useCallback(() => setIsHovered(false), []);

	// Effect for retrieving chat history
	useEffect(() => {
		const retrieveChatHistory = () => {
			try {
				const storedData = localStorage.getItem("messages");
				if (!client?.characterId || !storedData) {
					setSession("-1");
					setMessages([]);
					return;
				}

				const parsedData = JSON.parse(storedData);
				const characterIDs = Object.keys(parsedData);

				if (characterIDs.includes(client.characterId)) {
					const { sessionID, message } = parsedData[client.characterId];
					if (sessionID) setSession(sessionID);
					if (message) {
						const storedMessages = JSON.parse(message);
						setMessages(storedMessages);
					}
				} else {
					setMessages([]);
				}
			} catch (error) {
				console.error("Error retrieving chat history:", error);
				setMessages([]);
			}
		};

		retrieveChatHistory();
	}, [client?.characterId]);

	// Effect for storing chat history
	useEffect(() => {
		const storeChatHistory = () => {
			try {
				if (!client?.characterId || !messages.length) return;

				if (client?.convaiClient?.current && session === "-1" && client?.convaiClient?.current?.sessionId) {
					setSession(client.convaiClient.current.sessionId);
				}

				const messagesJSON = JSON.stringify(messages);
				const storedData = localStorage.getItem("messages");
				const parsedData = storedData ? JSON.parse(storedData) : {};

				parsedData[client.characterId] = {
					sessionID: session,
					message: messagesJSON,
				};

				localStorage.setItem("messages", JSON.stringify(parsedData));
			} catch (error) {
				console.error("Error storing chat history:", error);
			}
		};

		storeChatHistory();
	}, [client?.characterId, messages, session]);

	// Effect for handling user messages
	useEffect(() => {
		if (!client?.userText || !client?.userEndOfResponse) return;

		const newMessage = {
			sender: "user",
			content: DOMPurify.sanitize(client.userText),
		};

		setMessages(prevMessages => [...prevMessages, newMessage]);
		client.setUserEndOfResponse(false);

		// Clear timeout if it exists
		if (timer.current) {
			clearTimeout(timer.current);
		}

		// Set new timeout for error handling
		timer.current = setTimeout(() => {
			setErrorResponse(true);
			setMessages(prevMessages => [...prevMessages, {
				sender: "npc",
				content: "Sorry, the response is taking longer than expected. Please try again.",
				error: true
			}]);
		}, 10000); // Increased timeout to 10 seconds

		return () => {
			if (timer.current) {
				clearTimeout(timer.current);
			}
		};
	}, [client?.userEndOfResponse, client?.userText, client?.setUserEndOfResponse]);

	// Effect for handling NPC messages
	useEffect(() => {
		if (errorResponse) {
			const newMessage = {
				sender: "npc",
				content: errorMessage,
				error: true
			};
			setMessages(prevMessages => [...prevMessages, newMessage]);
			setErrorResponse(false);
			return;
		}

		if (!client?.npcText) return;

		if (!client?.isTalking) {
			const newMessage = {
				sender: "npc",
				content: DOMPurify.sanitize(client.npcText),
			};
			setMessages(prevMessages => [...prevMessages, newMessage]);

			// Stop audio when NPC finishes talking
			if (client.stopRecording) {
				client.stopRecording();
			}
		}

		setErrorResponse(false);
		if (timer.current) {
			clearTimeout(timer.current);
		}
	}, [client?.isTalking, errorResponse, client?.npcText, client?.stopRecording]);

	// Memoized styles
	const resetButtonStyle = useMemo(() => ({
		backgroundColor: isHovered ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.7)",
		width: "8vw",
		height: "2.5vw",
		color: "white",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		cursor: "pointer",
		marginBottom: "10px",
		borderRadius: "10px",
		transition: "background-color 0.3s ease"
	}), [isHovered]);

	const toggleLanguage = () => {
		setLanguage((prev) => (prev === "en" ? "ar" : "en"));
	};

	return (
		<ErrorBoundary FallbackComponent={ErrorFallback}>
			<section className="ChatBubble">
				<div style={{ display: "flex", marginTop: "2rem" }}>
					<button
						className="reset-button"
						onClick={ResetHistory}
						disabled={isProcessing}
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M4 4V10H10"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M20 20V14H14"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
							<path
								d="M14 4C16.6264 4 18.9552 5.28047 20.3256 7.17241C21.696 9.06434 21.9998 11.3464 21.1818 13.4484C20.3638 15.5504 18.5101 17.2279 16.1962 17.9139C13.8823 18.5999 11.3777 18.2213 9.39343 16.8787L4 12M20 12L14.6066 7.12126C12.6223 5.77868 10.1177 5.40008 7.8038 6.08608C5.48994 6.77208 3.6362 8.44958 2.8182 10.5516C2.00019 12.6536 2.30402 14.9357 3.67444 16.8276C5.04486 18.7195 7.37363 20 10 20"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
						Reset Chat
					</button>
				</div>
				{chatHistory === "Show" && (
					<ChatHistory
						history={history}
						messages={messages}
						showHistory={showHistory}
						npcName={client?.npcName || "AI Assistant"}
						userName={client?.userName || "You"}
					/>
				)}
				<ChatBubblev1
					npcText={client?.npcText}
					userText={client?.userText}
					startRecording={client.startRecording}
					onSendMessage={handleSendMessage}
					stopRecording={client.stopRecording}
					isProcessing={isProcessing}
					client={client}
				/>
			</section>
		</ErrorBoundary>
	);
};

export default React.memo(ChatBubble);
